import { useState, useEffect } from 'react'

// Approximate exchange rates from USD
const EXCHANGE_RATES: Record<string, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  CAD: 1.36,
  AUD: 1.52,
  MXN: 16.8,
  ARS: 880,
  COP: 3900,
  BRL: 5.1,
  CLP: 950,
  PEN: 3.7,
  UYU: 38.5,
}

// Currency symbols
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'CA$',
  AUD: 'A$',
  MXN: 'MX$',
  ARS: 'AR$',
  COP: 'COL$',
  BRL: 'R$',
  CLP: 'CLP$',
  PEN: 'S/',
  UYU: '$U',
}

// Country code to currency mapping
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  US: 'USD',
  CA: 'CAD',
  GB: 'GBP',
  AU: 'AUD',
  MX: 'MXN',
  AR: 'ARS',
  CO: 'COP',
  BR: 'BRL',
  CL: 'CLP',
  PE: 'PEN',
  UY: 'UYU',
  // EU countries
  DE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  PT: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  LU: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  EE: 'EUR',
  LV: 'EUR',
  LT: 'EUR',
  MT: 'EUR',
  CY: 'EUR',
  HR: 'EUR',
}

// Timezone to country mapping for common timezones
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA',
  'America/Halifax': 'CA',
  'America/St_Johns': 'CA',
  'America/Regina': 'CA',
  'America/Montreal': 'CA',
  'America/Mexico_City': 'MX',
  'America/Cancun': 'MX',
  'America/Monterrey': 'MX',
  'America/Tijuana': 'MX',
  'America/Chihuahua': 'MX',
  'America/Merida': 'MX',
  'America/Bogota': 'CO',
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Argentina/Cordoba': 'AR',
  'America/Argentina/Mendoza': 'AR',
  'America/Sao_Paulo': 'BR',
  'America/Fortaleza': 'BR',
  'America/Recife': 'BR',
  'America/Santiago': 'CL',
  'America/Lima': 'PE',
  'America/Montevideo': 'UY',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'Europe/London': 'GB',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Vienna': 'AT',
  'Europe/Lisbon': 'PT',
  'Europe/Dublin': 'IE',
  'Europe/Helsinki': 'FI',
  'Europe/Athens': 'GR',
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU',
  'Australia/Adelaide': 'AU',
}

// Cache
let cachedCurrency: string | null = null

// Detect currency from browser signals (instant, no network needed)
function detectFromBrowser(): string {
  // 1. Try timezone first (most reliable for country detection)
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz && TIMEZONE_TO_COUNTRY[tz]) {
      const country = TIMEZONE_TO_COUNTRY[tz]
      const currency = COUNTRY_TO_CURRENCY[country]
      if (currency && EXCHANGE_RATES[currency]) {
        console.log(
          '[useCurrency] Detected via timezone:',
          currency,
          `(${tz} → ${country})`,
        )
        return currency
      }
    }
  } catch (e) {
    // ignore
  }

  // 2. Try navigator.languages (e.g. ["en-CA", "en-US", "en"])
  try {
    const languages = navigator.languages || [navigator.language]
    for (const lang of languages) {
      const parts = lang.split('-')
      if (parts.length >= 2) {
        const countryCode = parts[parts.length - 1].toUpperCase()
        const currency = COUNTRY_TO_CURRENCY[countryCode]
        if (currency && EXCHANGE_RATES[currency]) {
          console.log(
            '[useCurrency] Detected via browser language:',
            currency,
            `(${lang} → ${countryCode})`,
          )
          return currency
        }
      }
    }
  } catch (e) {
    // ignore
  }

  // 3. Try Intl locale
  try {
    const locale = new Intl.NumberFormat().resolvedOptions().locale
    if (locale) {
      const parts = locale.split('-')
      if (parts.length >= 2) {
        const countryCode = parts[parts.length - 1].toUpperCase()
        const currency = COUNTRY_TO_CURRENCY[countryCode]
        if (currency && EXCHANGE_RATES[currency]) {
          console.log(
            '[useCurrency] Detected via Intl locale:',
            currency,
            `(${locale} → ${countryCode})`,
          )
          return currency
        }
      }
    }
  } catch (e) {
    // ignore
  }

  console.log(
    '[useCurrency] Browser detection found no match, defaulting to USD',
  )
  return 'USD'
}

export function useCurrency() {
  // Initialize immediately from browser signals (no loading needed)
  const [currency, setCurrency] = useState<string>(() => {
    if (cachedCurrency) return cachedCurrency
    const detected = detectFromBrowser()
    cachedCurrency = detected
    return detected
  })

  // No loading state needed since browser detection is synchronous
  const isLoading = false

  // Optionally try IP API in background to confirm/upgrade detection
  useEffect(() => {
    const tryIpDetection = async () => {
      try {
        const res = await fetch('https://ipwho.is/', {
          signal: AbortSignal.timeout(5000),
        })
        if (res.ok) {
          const data = await res.json()
          if (data.success !== false && data.currency?.code) {
            const ipCurrency = data.currency.code
            if (EXCHANGE_RATES[ipCurrency] && ipCurrency !== cachedCurrency) {
              console.log(
                '[useCurrency] IP API upgraded currency:',
                cachedCurrency,
                '→',
                ipCurrency,
              )
              cachedCurrency = ipCurrency
              setCurrency(ipCurrency)
            }
          }
        }
      } catch (e) {
        // IP detection is optional, browser detection is already active
      }
    }

    tryIpDetection()
  }, [])

  const formatPrice = (usdAmount: number, forceDecimals?: boolean) => {
    const rate = EXCHANGE_RATES[currency] || 1
    const converted = usdAmount * rate
    const symbol = CURRENCY_SYMBOLS[currency] || '$'

    const isLargeCurrency = rate > 100
    const needsDecimals =
      forceDecimals !== undefined
        ? forceDecimals
        : (!isLargeCurrency && usdAmount % 1 !== 0) ||
          (!isLargeCurrency && usdAmount < 100)

    return `${symbol}${converted.toLocaleString(undefined, {
      minimumFractionDigits: needsDecimals ? 2 : 0,
      maximumFractionDigits: needsDecimals ? 2 : 0,
    })}`
  }

  const formatPriceRange = (usdAmount: number, suffix = '') => {
    return `${formatPrice(usdAmount, false)}${suffix}`
  }

  return { currency, formatPrice, formatPriceRange, isLoading }
}
